import { Box, Button, Group, LoadingOverlay, Modal, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useResendMembershipInvite } from '@/queries/workspaceQueries'
import { showToast } from '@/theme/notifications'
import { Workspace, WorkspaceMember } from '@/types/workspace'

type ResendInviteModalProps = {
  opened: boolean
  member?: WorkspaceMember
  workspace: Workspace
  onClose: () => void
}

export const ResendInviteModal = ({
  opened,
  member,
  workspace,
  onClose
}: ResendInviteModalProps) => {
  const intl = useIntl()

  const { mutateAsync: resendInvite, isPending } = useResendMembershipInvite()

  const handleOnResendInvite = async () => {
    if (!member) {
      return
    }

    try {
      await resendInvite({
        workspaceId: workspace.id,
        memberId: member.id
      })

      showToast(
        intl.formatMessage(
          {
            id: 'workspace.members.invite.resend.success'
          },
          {
            email: member.user.email,
            workspaceName: workspace.name
          }
        ),
        'success'
      )
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage(
            { id: 'workspace.members.invite.resend.error' },
            {
              email: member.user.email
            }
          )
        ),
        'error'
      )
    }

    onClose()
  }

  return (
    <Modal
      size="lg"
      opened={opened}
      title={<FormattedMessage id="workspace.members.invite.resend" />}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        {member && (
          <Text size="sm" mb="lg">
            <FormattedMessage
              id="workspace.members.invite.resend.confirmation"
              values={{
                email: <strong>{member.user.email}</strong>,
                workspaceName: <strong>{workspace.name}</strong>
              }}
            />
          </Text>
        )}

        <Group justify="end">
          <Button miw={120} variant="default" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button miw={120} onClick={() => void handleOnResendInvite()}>
            <FormattedMessage id="workspace.members.invite.resend" />
          </Button>
        </Group>
      </Box>
    </Modal>
  )
}
